import { fromColorPathToColor } from '@weenat/theme'
import { useIntl } from '@weenat/wintl'
import { isNil } from 'lodash-es'
import { FC } from 'react'
import SvgArrowUp from './icons/generic-icons/ArrowUp'

const CX = 50
const CY = 50

const ARROW_SIZE = 32
const HALF_ARROW = ARROW_SIZE / 2

const ARROW_X = CX - HALF_ARROW
const ARROW_Y = CY - HALF_ARROW

const TICK_COUNT = 72

const INNER_RADIUS = 42
const OUTER_RADIUS = 48

const ticks = Array.from({ length: TICK_COUNT }, (_, i) => {
  const angleDeg = (360 / TICK_COUNT) * i
  const isCardinal = angleDeg % 45 === 0
  const angleRad = (angleDeg * Math.PI) / 180

  return {
    angleDeg,
    x1: CX + INNER_RADIUS * Math.sin(angleRad),
    y1: CY - INNER_RADIUS * Math.cos(angleRad),
    x2: CX + OUTER_RADIUS * Math.sin(angleRad),
    y2: CY - OUTER_RADIUS * Math.cos(angleRad),
    isCardinal
  }
})

interface CompassProps {
  windOrigin: number | null | undefined
  size?: number
}

const Compass: FC<CompassProps> = ({ windOrigin, size = 128 }) => {
  const { t } = useIntl()

  const minorStroke = fromColorPathToColor('grayscale.300')
  const cardinalStroke = fromColorPathToColor('grayscale.black')

  return (
    <svg viewBox='0 0 100 100' height={size} width={size}>
      <text fontSize={11} fontWeight={600} textAnchor='middle' x={CX} y={20}>
        {t('windDirections.north_abbr', { capitalize: true })}
      </text>
      <text fontSize={11} fontWeight={600} textAnchor='middle' x={CX} y={88}>
        {t('windDirections.south_abbr', { capitalize: true })}
      </text>
      <text fontSize={11} fontWeight={600} textAnchor='middle' x={18} y={CY + 4}>
        {t('windDirections.west_abbr', { capitalize: true })}
      </text>
      <text fontSize={11} fontWeight={600} textAnchor='middle' x={84} y={CY + 4}>
        {t('windDirections.east_abbr', { capitalize: true })}
      </text>
      {ticks.map((tick) => (
        <line
          key={tick.angleDeg}
          x1={tick.x1}
          y1={tick.y1}
          x2={tick.x2}
          y2={tick.y2}
          stroke={tick.isCardinal ? cardinalStroke : minorStroke}
          strokeWidth={tick.isCardinal ? 1.5 : 1}
          strokeLinecap='round'
        />
      ))}
      {!isNil(windOrigin) && (
        <g
          transform={`rotate(${windOrigin + 180} ${CX} ${CY}) translate(${ARROW_X} ${ARROW_Y})`}
        >
          <SvgArrowUp height={ARROW_SIZE} width={ARROW_SIZE} fill={cardinalStroke} />
        </g>
      )}
    </svg>
  )
}

export default Compass
